import React, { Component } from 'react'
import { connect } from 'react-redux'
import { saveAs } from 'file-saver'
import { Q } from '@nozbe/watermelondb'
import {
  Alert,
  Button,
  Box,
  Chip,
  Checkbox,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  LinearProgress,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from '@mui/material'
import fnsformat from 'date-fns/format'
import ptBr from 'date-fns/locale/pt-BR'
import compact from 'lodash/compact'
import sortBy from 'lodash/sortBy'

import database, { AVAILABLE_ENTITIES, utils } from '../../database'
import { recordError, recordEvent } from '../../observability/events'
import { withRouter } from '../../utils/withRouterCompat'
import { endLoading, startLoading } from '../../redux/screen/actions'
import { FAZENDA, LOTE, RETIRO, formatName } from '../../utils/formHelper'
import { isInDGRoster } from '../../utils/dgRoster'
import ZoetisLogo from '../../assets/img/logozoetis.png'
import AppContent from '../../components/layout/AppContent'
import PaginatedTable, { CELL_TYPES } from '../../components/PaginatedTable'
import TitleBar from '../../components/TitleBar'
import TopBar from '../../components/TopBar'
import { countResumoDg, getDiagnosisType, isDiscardRow } from './resumoDgCounts'
import { ANALYSIS_ORDER, buildResumoDgAnalyses } from './resumoDgAnalysis'
import {
  DEFAULT_EXPORT_OPTIONS,
  EXPORT_COLUMNS,
  EXPORT_FILTERS,
  filterRowsForExport,
  normalizeExportOptions,
  validateExportOptions,
} from './resumoDgExport'
import {
  EXPORT_SCOPES,
  getBatchChainIds,
  getScopeRows,
  mergeBatchCows,
} from './resumoDgScope'

/**
 * Resumo DG offline — o papel que o veterinário deixa na fazenda logo depois do DG.
 *
 * Tem rota e loader próprios, separados do relatório online (`pages/new-reports`). Enquanto
 * as duas telas dividiam a rota `/report` e decidiam qual renderizar lendo um rótulo de manejo
 * do state de navegação, o acoplamento produziu uma família inteira de bugs — botão aparecendo
 * em manejo que não é DG, e relatório geral virando Resumo quando o aparelho estava sem rede.
 *
 * É importado estaticamente no `App.js` pela convenção das telas de campo (`DG`, `DGFinal`,
 * `Batch`, `Farm` também são), não por necessidade de offline: o Workbox pré-cacheia os chunks
 * lazy. Já o `resumoDgPdf` (jsPDF + autotable, ~140 kB gz) fica sob `import()` dinâmico, porque
 * só faz falta no clique, não para a tela renderizar.
 */

const RESUMO_DG_TITLE = 'Resumo DG'

const formatResumoDate = (value) =>
  value ? fnsformat(value, 'dd/MM/yyyy', { locale: ptBr }) : '—'

const firstNonEmpty = (...values) =>
  values.find(
    (value) =>
      value !== null &&
      value !== undefined &&
      value !== '' &&
      value !== '[N/A]',
  ) ?? null

const buildLabelMap = (values = []) =>
  new Map(values.map((item) => [item.id, item.valor]))

const resolveLabel = (map, id) => {
  if (!id) return ''
  return map.get(id) || id
}

const getPrimitiveId = (value) => {
  if (value === null || value === undefined || value === '') return null
  if (typeof value === 'string' || typeof value === 'number') return value
  return value.id || value._raw?.id || null
}

const loadLogoDataUrl = async () => {
  const response = await fetch(ZoetisLogo)
  if (!response.ok)
    throw new Error('Não foi possível carregar a logo da Zoetis')
  const blob = await response.blob()

  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onloadend = () => resolve(reader.result)
    reader.onerror = reject
    reader.readAsDataURL(blob)
  })
}

const formatSemenLabel = (partida, bull) => {
  if (!partida && !bull) return ''
  const bullName = bull?.nome || ''
  const partidaName =
    partida?.codigo ||
    (partida?.data ? formatResumoDate(new Date(partida.data)) : '')
  return [bullName, partidaName].filter(Boolean).join(' - ')
}

const resolveRows = (rows, maps, semenByCowId, currentDgCowIds) =>
  rows.map((cow) => {
    // O Resumo é sempre do DG, mesmo quando aberto pelo DG Final — é para lá que o
    // veterinário precisa poder olhar depois que o sistema libera o manejo seguinte.
    const diagnosisId = getPrimitiveId(cow.condicaoDG)
    const diagnosisLabelId = firstNonEmpty(
      getPrimitiveId(cow.condicaoDG),
      getPrimitiveId(cow.condicaoDGFinal),
    )
    const eccId = firstNonEmpty(
      getPrimitiveId(cow.eccDG),
      getPrimitiveId(cow.ecc),
      getPrimitiveId(cow.eccFinal),
    )
    const observation = firstNonEmpty(cow.observacaoDG, cow.observacaoDGFinal)
    const dataDg = firstNonEmpty(cow.dataDGAt, cow.dataDGFinalAt)

    return {
      id: cow.id,
      codVaca: cow.codVaca || '—',
      diagnostico:
        resolveLabel(maps.condicaoDG, diagnosisLabelId) || 'Pendente',
      ecc: resolveLabel(maps.ecc, eccId) || '—',
      ordemMatriz:
        resolveLabel(maps.ordemMatriz, getPrimitiveId(cow.ordemMatriz)) || null,
      racaMatriz:
        resolveLabel(maps.racaMatriz, getPrimitiveId(cow.racaMatriz)) || null,
      touro: semenByCowId.get(cow.id)?.touro || null,
      partida: semenByCowId.get(cow.id)?.partida || null,
      semen: semenByCowId.get(cow.id)?.label || '—',
      observacao: observation || '',
      isSaved: Boolean(diagnosisLabelId || dataDg),
      diagnosisType: getDiagnosisType({
        condicaoDG: diagnosisId,
        isSaved: Boolean(diagnosisLabelId || dataDg),
      }),
      isDiscard: isDiscardRow({
        condicaoDG: diagnosisId,
        isSaved: Boolean(diagnosisLabelId || dataDg),
      }),
      condicaoDG: diagnosisId,
      dataDg,
      loteId: cow.loteId || cow.batchId || cow._raw?.batch_id,
      isCurrentDg: currentDgCowIds.has(cow.id),
    }
  })

const getIatfValue = (record, dtoName, columnName) =>
  record[dtoName] ?? record._raw?.[columnName]

const byCreatedAtAsc = (a, b) =>
  (a.createdAt?.valueOf?.() ?? 0) - (b.createdAt?.valueOf?.() ?? 0)

/**
 * Uma leitura só de `iatfs`/`dns`, com duas seleções diferentes:
 *
 * - `firstIatf`/`firstDn` alimentam o roster. A tela de DG olha `iatf[0]`/`dns[0]` da consulta
 *   por matriz, então o Resumo olha o registro mais antigo — mesma matriz entra ou sai nos dois.
 * - `latestIatf` alimenta a coluna Sêmen. Em lote de ressinc a matriz tem IATF do lote pai e do
 *   filho; a inseminação que o DG em curso diagnostica é a última.
 *
 * `isDeleted` é filtrado aqui porque a query direta na collection não passa pelo Repository,
 * que faria isso por conta própria.
 */
const loadIatfsAndDnsByCow = async (cows) => {
  const empty = {
    firstIatfByCow: new Map(),
    latestIatfByCow: new Map(),
    firstDnByCow: new Map(),
  }
  if (cows.length === 0) return empty

  const cowIds = cows.map((cow) => cow.id)
  const [iatfs, dns] = await Promise.all([
    database.collections
      .get('iatfs')
      .query(Q.where('vacaId', Q.oneOf(cowIds)), Q.where('isDeleted', false))
      .fetch(),
    database.collections
      .get('dns')
      .query(Q.where('vacaId', Q.oneOf(cowIds)), Q.where('isDeleted', false))
      .fetch(),
  ])

  const firstIatfByCow = new Map()
  const latestIatfByCow = new Map()
  const firstDnByCow = new Map()

  ;[...iatfs].sort(byCreatedAtAsc).forEach((iatf) => {
    const vacaId = getIatfValue(iatf, 'vacaId', 'vacaId')
    if (!vacaId) return
    if (!firstIatfByCow.has(vacaId)) firstIatfByCow.set(vacaId, iatf)
    latestIatfByCow.set(vacaId, iatf)
  })
  ;[...dns].sort(byCreatedAtAsc).forEach((dn) => {
    const vacaId = getIatfValue(dn, 'vacaId', 'vacaId')
    if (vacaId && !firstDnByCow.has(vacaId)) firstDnByCow.set(vacaId, dn)
  })

  return { firstIatfByCow, latestIatfByCow, firstDnByCow }
}

const loadSemenByCowId = async (latestIatfByCow, farmId) => {
  const iatfs = [...latestIatfByCow.values()]
  if (iatfs.length === 0) return new Map()

  const partidaIds = [
    ...new Set(
      iatfs
        .map((iatf) => getIatfValue(iatf, 'partidaId', 'bull_id'))
        .filter(Boolean),
    ),
  ]
  if (partidaIds.length === 0) return new Map()

  const partidas = await utils.getWithParam(
    AVAILABLE_ENTITIES.PARTIDA,
    'farm_id',
    farmId,
  )
  const partidaById = new Map(partidas.map((partida) => [partida.id, partida]))
  const bullIds = [
    ...new Set(
      partidas
        .filter((partida) => partidaIds.includes(partida.id))
        .map((partida) => partida.touroId)
        .filter(Boolean),
    ),
  ]
  const bullRows = bullIds.length
    ? await utils.getWithParam(
        AVAILABLE_ENTITIES.BULLS_NEW,
        'id',
        Q.oneOf(bullIds),
      )
    : []
  const bullById = new Map(bullRows.map((bull) => [bull.id, bull]))
  const semenByCowId = new Map()

  iatfs.forEach((iatf) => {
    const partidaId = getIatfValue(iatf, 'partidaId', 'bull_id')
    const vacaId = getIatfValue(iatf, 'vacaId', 'vacaId')
    const partida = partidaById.get(partidaId)
    const bull = partida && bullById.get(partida.touroId)
    const label = formatSemenLabel(partida, bull)
    if (label && vacaId)
      semenByCowId.set(vacaId, {
        label,
        touro: bull?.nome || null,
        partida: partida?.codigo || null,
      })
  })

  return semenByCowId
}

const RESUMO_FILTERS = [
  { key: 'all', label: 'Todas' },
  { key: 'pending', label: 'Pendentes' },
  { key: 'pregnant', label: 'Prenhes' },
  { key: 'empty', label: 'Vazias' },
  { key: 'discarded', label: 'Descartes' },
]

// 'discarded' recorta prenhes e vazias em vez de excluí-las — por isso não é um `type`.
const matchesFilter = (row, filterKey) => {
  if (filterKey === 'all') return true
  if (filterKey === 'discarded') return isDiscardRow(row)
  return getDiagnosisType(row) === filterKey
}

const getFilterCount = (counts, filterKey) =>
  filterKey === 'all' ? counts.total : counts[filterKey]

const getDiagnosisChipColor = (type) => {
  if (type === 'pregnant') return 'success'
  if (type === 'empty') return 'info'
  if (type === 'pending') return 'warning'
  return 'default'
}

class ResumoDg extends Component {
  state = {
    analysis: 'general',
    exportOptions: DEFAULT_EXPORT_OPTIONS,
    filter: 'all',
    pdfError: null,
    showMatrixList: true,
    exportModal: null,
    exportRows: [],
  }

  constructor(props) {
    super(props)
    // `propsFromDatabase` abre o loading; sem este par a sobreposição nunca some.
    props.endLoading()
  }

  // jsPDF + autotable pesam ~140 kB gz e não são necessários para renderizar a tela.
  // O Workbox pré-cacheia este chunk, então o clique funciona offline.
  async withPdfModule(action) {
    try {
      const pdfModule = await import('./resumoDgPdf')
      let logoDataUrl = null
      try {
        logoDataUrl = await loadLogoDataUrl()
      } catch (error) {
        recordError(error, {
          category: 'outro',
          action: 'resumo_dg_pdf_logo_failed',
        })
      }
      this.setState({ pdfError: null })
      return action(pdfModule, logoDataUrl)
    } catch (error) {
      recordError(error, {
        category: 'outro',
        action: 'resumo_dg_pdf_module_failed',
      })
      this.setState({
        pdfError:
          'Não foi possível carregar o gerador de PDF. Reconecte uma vez para que o app termine de baixar seus arquivos.',
      })
      return null
    }
  }

  buildPdfData(rows, logoDataUrl, exportOptions) {
    const options = normalizeExportOptions(exportOptions)
    const scopedRows = getScopeRows(rows, options.scope)
    const selectedRows = filterRowsForExport(
      scopedRows,
      options.matrixList.filter,
    )
    return {
      analyses: buildResumoDgAnalyses(scopedRows),
      farm: this.props.farm,
      corral: this.props.corral,
      batch: this.props.batch,
      rows: selectedRows,
      fullTotal: rows.length,
      scopeLabel:
        EXPORT_SCOPES.find((scope) => scope.value === options.scope)?.label ||
        'Lote completo',
      exportOptions: options,
      stats: countResumoDg(scopedRows),
      connection: this.props.connection || 'Offline',
      logoDataUrl,
    }
  }

  openExportModal = (mode, rows) =>
    this.setState({
      exportModal: mode,
      exportRows: rows,
      exportOptions: normalizeExportOptions(this.state.exportOptions),
    })

  updateExportOptions = (update) =>
    this.setState((state) => ({
      exportOptions: normalizeExportOptions(update(state.exportOptions)),
    }))

  confirmExport = () => {
    const validation = validateExportOptions(this.state.exportOptions)
    if (!validation.valid) {
      this.setState({ pdfError: validation.errors.join(' ') })
      return
    }

    const mode = this.state.exportModal
    this.setState({ exportModal: null })
    if (mode === 'print')
      return this.handlePrint(this.state.exportRows, validation.options)
    return this.handleDownload(this.state.exportRows, validation.options)
  }

  handleDownload = (rows, exportOptions) =>
    this.withPdfModule(
      ({ createResumoDgPdf, getResumoDgPdfFilename }, logoDataUrl) => {
        const pdf = createResumoDgPdf(
          this.buildPdfData(rows, logoDataUrl, exportOptions),
        )
        saveAs(pdf.output('blob'), getResumoDgPdfFilename(this.props.batch))
        recordEvent({
          level: 'info',
          category: 'outro',
          action: 'resumo_dg_pdf_downloaded',
        })
      },
    )

  handlePrint = (rows, exportOptions) => {
    const printWindow = window.open('', '_blank')
    if (!printWindow) {
      this.setState({
        pdfError:
          'O navegador bloqueou a janela de impressão. Use "Baixar PDF" — o visualizador do aparelho imprime e compartilha o arquivo.',
      })
      recordError(
        new Error('Não foi possível abrir a janela de impressão do Resumo DG'),
        { category: 'outro', action: 'resumo_dg_pdf_print_blocked' },
      )
      return
    }

    return this.withPdfModule(({ createResumoDgPdf }, logoDataUrl) => {
      const pdf = createResumoDgPdf(
        this.buildPdfData(rows, logoDataUrl, exportOptions),
      )
      const pdfUrl = URL.createObjectURL(pdf.output('blob'))
      printWindow.location.href = pdfUrl
      // Sem isto o blob fica retido enquanto a aba do app viver. O atraso dá tempo de a
      // outra janela carregar antes de o endereço deixar de resolver.
      setTimeout(() => URL.revokeObjectURL(pdfUrl), 60000)
      recordEvent({
        level: 'info',
        category: 'outro',
        action: 'resumo_dg_pdf_printed',
      })
    })
  }

  render() {
    if (!this.props.farm) {
      return (
        <AppContent>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              flexDirection: 'column',
              gap: 2,
              minHeight: '50vh',
              p: 4,
              textAlign: 'center',
            }}
          >
            <Box sx={{ fontSize: 16, color: 'error.main' }}>
              Não foi possível carregar os dados da fazenda.
            </Box>
            <Box sx={{ fontSize: 14, color: 'text.secondary' }}>
              Verifique se a fazenda foi baixada para uso offline e tente
              novamente.
            </Box>
          </Box>
        </AppContent>
      )
    }

    const rows = this.props.rows || []
    const paths = this.props.paths || []
    const connection = this.props.connection || 'Offline'

    // Fonte única: tela e PDF leem daqui, e a decomposição é a mesma da tela de DG —
    // prenhes/vazias já incluem seus descartes, que aparecem como anotação transversal.
    const counts = countResumoDg(rows)
    const { total, saved, pending, pregnant, empty } = counts
    const pregnantRate = counts.pregnancyRate.toFixed(1)
    const diagnosisProgress =
      total > 0 ? Math.min(100, (saved / total) * 100) : 0

    const selectedFilter = this.state.filter
    const visibleRows = rows.filter((row) => matchesFilter(row, selectedFilter))
    const analyses = buildResumoDgAnalyses(rows)
    const diagnosisChip = (row) => {
      const type = getDiagnosisType(row)
      return (
        <Chip
          label={row.diagnostico || 'Pendente'}
          color={isDiscardRow(row) ? 'error' : getDiagnosisChipColor(type)}
          variant={type === 'pending' ? 'filled' : 'outlined'}
          size='small'
          sx={{ fontWeight: 700, minHeight: 32 }}
        />
      )
    }
    const columns = [
      {
        name: 'Matriz',
        type: CELL_TYPES.TEXT,
        label: (row) => row.codVaca,
        sortBy: true,
        sortValue: (row) => row.codVaca,
      },
      {
        name: 'Resultado',
        type: CELL_TYPES.TEXT,
        label: diagnosisChip,
        sortBy: true,
        sortValue: (row) => row.diagnostico,
      },
      {
        name: 'ECC',
        type: CELL_TYPES.TEXT_CENTER,
        label: (row) => String(row.ecc ?? '—'),
        sortBy: true,
        sortValue: (row) => row.ecc ?? '',
      },
      {
        name: 'Sêmen',
        type: CELL_TYPES.TEXT_CENTER,
        label: (row) => String(row.semen ?? '—'),
        sortBy: true,
        sortValue: (row) => row.semen ?? '',
      },
      {
        name: 'Observação',
        type: CELL_TYPES.TEXT,
        label: (row) => String(row.observacao ?? '—'),
        sortBy: true,
        sortValue: (row) => row.observacao ?? '',
      },
    ]

    return (
      <AppContent>
        <TopBar className='hidewhenprinting' title={RESUMO_DG_TITLE} />
        <TitleBar
          className='hidewhenprinting'
          title={RESUMO_DG_TITLE}
          buttons={[
            {
              label: 'Baixar PDF',
              onClick: () => this.openExportModal('download', rows),
            },
            {
              label: 'Imprimir',
              onClick: () => this.openExportModal('print', rows),
            },
          ]}
          paths={paths}
        />

        <Box sx={{ mt: 2, maxWidth: 'lg', mx: 'auto', px: { xs: 1.5, sm: 2 } }}>
          {this.state.pdfError && (
            <Alert severity='warning' sx={{ mb: 2 }}>
              {this.state.pdfError}
            </Alert>
          )}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 1,
              mb: 2,
              flexWrap: 'wrap',
            }}
          >
            <Box
              component='img'
              src={ZoetisLogo}
              alt='Zoetis'
              sx={{ width: { xs: 120, sm: 150 }, height: 'auto' }}
            />
            <Box>
              <Typography variant='h6' sx={{ fontWeight: 800 }}>
                {RESUMO_DG_TITLE}
              </Typography>
              <Typography variant='body2' color='text.secondary'>
                {total} matrizes ·{' '}
                {formatResumoDate(rows.find((row) => row.dataDg)?.dataDg)}
              </Typography>
            </Box>
            <Chip
              label={connection}
              size='small'
              variant='outlined'
              color={connection === 'Online' ? 'success' : 'warning'}
            />
          </Box>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
              gap: 1.5,
              mb: 2,
              p: { xs: 1.5, sm: 2 },
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              bgcolor: 'background.paper',
            }}
          >
            {[
              ['Fazenda', this.props.farm?.nome],
              ['Retiro', this.props.corral?.nome],
              ['Lote', this.props.batch?.nomeLote],
            ].map(([label, value]) => (
              <Box key={label}>
                <Typography
                  variant='overline'
                  sx={{
                    color: 'text.secondary',
                    fontWeight: 800,
                    letterSpacing: 1,
                  }}
                >
                  {label}
                </Typography>
                <Typography
                  variant={label === 'Lote' ? 'h6' : 'body1'}
                  sx={{ fontWeight: label === 'Lote' ? 900 : 700 }}
                >
                  {value || 'Não informado'}
                </Typography>
              </Box>
            ))}
          </Box>

          <Box
            sx={{
              mb: 2,
              p: { xs: 1.5, sm: 2 },
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              bgcolor: 'background.paper',
            }}
          >
            <Typography variant='subtitle1' sx={{ fontWeight: 900, mb: 1.25 }}>
              Análises básicas
            </Typography>
            <Stack direction='row' spacing={1} useFlexGap flexWrap='wrap'>
              {ANALYSIS_ORDER.map((key) => (
                <Chip
                  key={key}
                  label={analyses[key].label}
                  onClick={() => this.setState({ analysis: key })}
                  color={this.state.analysis === key ? 'primary' : 'default'}
                  variant={this.state.analysis === key ? 'filled' : 'outlined'}
                  sx={{ minHeight: 40, fontWeight: 800 }}
                />
              ))}
            </Stack>
            <AnalysisTable analysis={analyses[this.state.analysis]} />
          </Box>

          <Box
            sx={{
              mb: 2,
              p: { xs: 2, sm: 2.5 },
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: 'background.paper',
            }}
          >
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              alignItems={{ sm: 'center' }}
            >
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant='overline'
                  sx={{ fontWeight: 800, letterSpacing: 1 }}
                >
                  Resultado principal
                </Typography>
                <Typography
                  variant='h3'
                  sx={{ fontWeight: 900, lineHeight: 1.1 }}
                >
                  {pregnantRate}%
                </Typography>
                <Typography variant='body2' color='text.secondary'>
                  {pregnant} prenhes · {empty} vazias
                </Typography>
                <Typography variant='body2' color='text.secondary'>
                  Descartes: {counts.discardedPregnant} Prenhes ·{' '}
                  {counts.discardedEmpty} Vazias
                </Typography>
              </Box>
              <Box sx={{ flex: 1, width: '100%' }}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    mb: 0.75,
                  }}
                >
                  <Typography variant='body2' sx={{ fontWeight: 700 }}>
                    Diagnósticos concluídos
                  </Typography>
                  <Typography variant='body2' sx={{ fontWeight: 800 }}>
                    {saved}/{total}
                  </Typography>
                </Box>
                <LinearProgress
                  variant='determinate'
                  value={diagnosisProgress}
                  sx={{ height: 10, borderRadius: 5 }}
                />
                {pending > 0 && (
                  <Typography
                    variant='body2'
                    sx={{ mt: 1, color: 'warning.dark', fontWeight: 800 }}
                  >
                    {pending} pendente{pending === 1 ? '' : 's'}
                  </Typography>
                )}
              </Box>
            </Stack>
          </Box>

          <Box
            sx={{
              mb: 1,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 1,
              flexWrap: 'wrap',
            }}
          >
            <Typography variant='subtitle1' sx={{ fontWeight: 900 }}>
              Lista de matrizes
            </Typography>
            <Chip
              label={
                this.state.showMatrixList ? 'Ocultar lista' : 'Exibir lista'
              }
              onClick={() =>
                this.setState((state) => ({
                  showMatrixList: !state.showMatrixList,
                }))
              }
              variant='outlined'
              color='primary'
              sx={{ fontWeight: 800 }}
            />
          </Box>

          <Box
            sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}
            aria-label='Filtrar matrizes'
          >
            {RESUMO_FILTERS.map((filter) => (
              <Chip
                key={filter.key}
                label={`${filter.label} (${getFilterCount(counts, filter.key)})`}
                onClick={() => this.setState({ filter: filter.key })}
                color={selectedFilter === filter.key ? 'primary' : 'default'}
                variant={selectedFilter === filter.key ? 'filled' : 'outlined'}
                sx={{ minHeight: 40, fontWeight: 700 }}
              />
            ))}
          </Box>

          {this.state.showMatrixList && (
            <Box sx={{ display: { xs: 'none', md: 'block' } }}>
              <PaginatedTable
                columns={columns}
                data={visibleRows}
                skipPagination
                size='small'
                noDataMessage='Nenhuma matriz corresponde a este filtro.'
                style={{ margin: 'auto', marginBottom: '80px', width: '100%' }}
              />
            </Box>
          )}
          {this.state.showMatrixList && (
            <Box sx={{ display: { xs: 'block', md: 'none' }, mb: 8 }}>
              {visibleRows.length === 0 && (
                <Typography
                  color='text.secondary'
                  sx={{ p: 2, textAlign: 'center' }}
                >
                  Nenhuma matriz corresponde a este filtro.
                </Typography>
              )}
              <Stack spacing={1.5}>
                {visibleRows.map((row) => (
                  <Box
                    key={row.id}
                    sx={{
                      p: 2,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 2,
                      bgcolor: 'background.paper',
                      minHeight: 120,
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        gap: 1,
                        alignItems: 'flex-start',
                      }}
                    >
                      <Typography sx={{ fontWeight: 900, fontSize: 18 }}>
                        {row.codVaca}
                      </Typography>
                      {diagnosisChip(row)}
                    </Box>
                    <Divider sx={{ my: 1.25 }} />
                    <Typography variant='body2' sx={{ fontWeight: 700 }}>
                      ECC: {row.ecc} · Sêmen: {row.semen}
                    </Typography>
                    {row.observacao && (
                      <Typography
                        variant='body2'
                        sx={{ mt: 1, color: 'text.primary' }}
                      >
                        {row.observacao}
                      </Typography>
                    )}
                  </Box>
                ))}
              </Stack>
            </Box>
          )}
        </Box>
        <ExportDialog
          open={Boolean(this.state.exportModal)}
          mode={this.state.exportModal}
          options={this.state.exportOptions}
          totalRows={rows.length}
          onChange={this.updateExportOptions}
          onCancel={() => this.setState({ exportModal: null })}
          onConfirm={this.confirmExport}
        />
      </AppContent>
    )
  }
}

const AnalysisTable = ({ analysis }) => {
  const columns = [
    ['Grupo', (row) => row.group],
    ['Total', (row) => row.total],
    ['Concluídas', (row) => `${row.saved}/${row.total}`],
    ['Pendentes', (row) => row.pending],
    ['Prenhes', (row) => row.pregnant],
    ['Vazias', (row) => row.empty],
    ['Taxa', (row) => `${row.pregnancyRate.toFixed(1)}%`],
  ]

  return (
    <Box sx={{ mt: 2, overflowX: 'auto' }}>
      <Box
        component='table'
        sx={{
          width: '100%',
          minWidth: 640,
          borderCollapse: 'collapse',
          '& th': {
            bgcolor: 'primary.dark',
            color: 'primary.contrastText',
            fontWeight: 900,
            textAlign: 'left',
          },
          '& th, & td': { border: '1px solid', borderColor: 'divider', p: 1 },
          '& tbody tr:nth-of-type(even)': { bgcolor: 'action.hover' },
        }}
      >
        <thead>
          <tr>
            {columns.map(([label]) => (
              <th key={label}>{label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {analysis.rows.map((row) => (
            <tr key={row.group}>
              {columns.map(([label, value]) => (
                <td key={label}>{value(row)}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </Box>
    </Box>
  )
}

const ExportDialog = ({
  open,
  mode,
  options,
  totalRows,
  onChange,
  onCancel,
  onConfirm,
}) => {
  const update = (path, value) =>
    onChange((current) => {
      const next = normalizeExportOptions(current)
      const [section, key] = path
      next[section][key] = value
      return next
    })

  return (
    <Dialog open={open} onClose={onCancel} fullWidth maxWidth='sm'>
      <DialogTitle>Configurar Resumo DG</DialogTitle>
      <DialogContent dividers>
        <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
          Os indicadores continuam calculados sobre todo o lote. A configuração
          abaixo define o conteúdo do documento.
        </Typography>

        <Typography variant='subtitle1' sx={{ fontWeight: 900 }}>
          Análises
        </Typography>
        <FormGroup>
          {[
            ['general', 'Geral'],
            ['order', 'Por Ordem'],
            ['breed', 'Por Raça'],
            ['bull', 'Por Touro'],
          ].map(([key, label]) => (
            <FormControlLabel
              key={key}
              control={
                <Checkbox
                  checked={Boolean(options.analyses[key])}
                  onChange={(event) =>
                    update(['analyses', key], event.target.checked)
                  }
                />
              }
              label={label}
            />
          ))}
        </FormGroup>

        <Typography variant='subtitle1' sx={{ fontWeight: 900, mt: 2 }}>
          Escopo do documento
        </Typography>
        <RadioGroup
          value={options.scope}
          onChange={(event) =>
            onChange((current) => ({
              ...normalizeExportOptions(current),
              scope: event.target.value,
            }))
          }
        >
          {EXPORT_SCOPES.map((scope) => (
            <FormControlLabel
              key={scope.value}
              value={scope.value}
              control={<Radio />}
              label={scope.label}
            />
          ))}
        </RadioGroup>

        <Typography variant='subtitle1' sx={{ fontWeight: 900, mt: 2 }}>
          Lista de matrizes
        </Typography>
        <FormControlLabel
          control={
            <Checkbox
              checked={options.matrixList.enabled}
              onChange={(event) =>
                update(['matrixList', 'enabled'], event.target.checked)
              }
            />
          }
          label={`Incluir lista (${totalRows} matrizes no lote)`}
        />
        {options.matrixList.enabled && (
          <>
            <RadioGroup
              value={options.matrixList.filter}
              onChange={(event) =>
                update(['matrixList', 'filter'], event.target.value)
              }
            >
              {EXPORT_FILTERS.map((filter) => (
                <FormControlLabel
                  key={filter.value}
                  value={filter.value}
                  control={<Radio />}
                  label={filter.label}
                />
              ))}
            </RadioGroup>
            <Typography variant='body2' sx={{ fontWeight: 700, mt: 1 }}>
              Colunas
            </Typography>
            <FormGroup>
              {EXPORT_COLUMNS.map((column) => (
                <FormControlLabel
                  key={column.key}
                  control={
                    <Checkbox
                      checked={Boolean(options.matrixList.columns[column.key])}
                      onChange={(event) =>
                        onChange((current) => {
                          const next = normalizeExportOptions(current)
                          next.matrixList.columns[column.key] =
                            event.target.checked
                          return next
                        })
                      }
                    />
                  }
                  label={column.label}
                />
              ))}
            </FormGroup>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>Cancelar</Button>
        <Button onClick={onConfirm} variant='contained'>
          {mode === 'print' ? 'Gerar e imprimir' : 'Gerar PDF'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

const mapDispatchToProps = (dispatch) => ({
  startLoading: () => dispatch(startLoading()),
  endLoading: () => dispatch(endLoading()),
})

const propsFromDatabase = async (props) => {
  props.startLoading()

  const [farm, corral, batch] = await Promise.all([
    utils.getElement(AVAILABLE_ENTITIES.FARMS, props.match.params.farmId),
    utils.getElement(AVAILABLE_ENTITIES.CORRALS, props.match.params.corralId),
    utils.getElement(AVAILABLE_ENTITIES.BATCHES, props.match.params.batchId),
  ])

  if (!farm || !batch)
    return { farm: null, corral: null, batch: null, rows: [] }

  const batches = await utils.getWithParam(
    AVAILABLE_ENTITIES.BATCHES,
    'corral_id',
    props.match.params.corralId,
  )
  const batchChainIds = getBatchChainIds(batches, batch.id)

  const [condicaoDG, condicaoDGFinal, ecc, ordemMatriz, racaMatriz] =
    await Promise.all([
      utils.getDomainValuesBy('Condição DG'),
      utils.getDomainValuesBy('Condição DG Final'),
      utils.getDomainValuesBy('ECC'),
      utils.getDomainValuesBy('Ordem da Matriz Predominante'),
      utils.getDomainValuesBy('Raça da Matriz predominante'),
    ])

  const allCows = await database.collections
    .get('d0s')
    .query(
      Q.where('batch_id', Q.oneOf(batchChainIds)),
      Q.where('isDeleted', false),
    )
    .fetch()

  const currentBatchCows = allCows.filter(
    (cow) => (cow.loteId || cow._raw?.batch_id) === batch.id,
  )
  const { firstIatfByCow, latestIatfByCow, firstDnByCow } =
    await loadIatfsAndDnsByCow(currentBatchCows)

  // Mesmo roster da tela de manejo: sem ele o "Concluídas X/Y" não fecha em lote de
  // ressinc e o denominador da taxa de prenhez fica maior que o lote real.
  const cows = sortBy(
    currentBatchCows.filter((cow) =>
      isInDGRoster(cow, {
        iatf: firstIatfByCow.get(cow.id),
        dn: firstDnByCow.get(cow.id),
      }),
    ),
    [(cow) => cow.codVaca || ''],
  )

  const currentDgCowIds = new Set(cows.map((cow) => cow.id))
  const completeCows = mergeBatchCows(allCows, batchChainIds)
  const { latestIatfByCow: allLatestIatfByCow } =
    await loadIatfsAndDnsByCow(completeCows)
  const semenByCowId = await loadSemenByCowId(
    new Map(
      compact(
        completeCows.map((cow) => {
          const iatf = allLatestIatfByCow.get(cow.id)
          return iatf ? [cow.id, iatf] : null
        }),
      ),
    ),
    farm.id,
  )

  return {
    farm,
    corral,
    batch,
    batchChainIds,
    rows: resolveRows(
      completeCows,
      {
        condicaoDG: buildLabelMap([...condicaoDG, ...condicaoDGFinal]),
        ecc: buildLabelMap(ecc),
        ordemMatriz: buildLabelMap(ordemMatriz),
        racaMatriz: buildLabelMap(racaMatriz),
      },
      semenByCowId,
      currentDgCowIds,
    ),
    connection:
      typeof window !== 'undefined' && !window.navigator.onLine
        ? 'Offline'
        : 'Online',
    paths: compact([
      { label: formatName(farm.nome, FAZENDA), route: `/farms/${farm.id}` },
      corral && {
        label: formatName(corral.nome, RETIRO),
        route: `/farms/${farm.id}/corrals/${corral.id}`,
      },
      {
        label: formatName(batch.nomeLote, LOTE),
        route: `/farms/${farm.id}/corrals/${corral?.id}/batches/${batch.id}`,
      },
      { label: RESUMO_DG_TITLE },
    ]),
  }
}

export default connect(
  null,
  mapDispatchToProps,
)(utils.withPropsFromDatabase(propsFromDatabase, withRouter(ResumoDg)))
